/**
 * Client HTTP unique du portail.
 *
 * Trois responsabilités, et rien d'autre :
 * 1. joindre systématiquement le cookie de session (`credentials: 'include'`) ;
 * 2. gérer le jeton anti-CSRF exigé par l'API sur toute mutation (§10) ;
 * 3. transformer une réponse d'erreur en `ApiError` en conservant **tel quel**
 *    le message rédigé par le serveur — il est écrit pour l'utilisateur final,
 *    le remplacer par un message générique ferait perdre de l'information.
 */

/** Détail d'un champ invalide, tel que renvoyé par la validation zod côté API. */
export interface ApiIssue {
  field?: string;
  message: string;
}

/** Erreur applicative portée par une réponse HTTP non 2xx. */
export class ApiError extends Error {
  /** Code HTTP, ou 0 lorsque la requête n'a même pas abouti. */
  public readonly status: number;
  /** Détail par champ, présent sur les erreurs de validation. */
  public readonly issues?: ApiIssue[];

  constructor(status: number, message: string, issues?: ApiIssue[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = issues;
  }
}

/** Méthodes considérées comme des mutations, donc protégées par le CSRF. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Jeton CSRF mémorisé entre deux mutations. */
let csrfToken: string | null = null;

/** Requête de jeton en cours, pour n'en émettre qu'une même en cas de rafale. */
let csrfRequest: Promise<string> | null = null;

/** Vide le cache du jeton (utilisé après une déconnexion). */
export function resetCsrfToken(): void {
  csrfToken = null;
  csrfRequest = null;
}

/** Récupère le jeton anti-CSRF auprès de l'API et le met en cache. */
async function fetchCsrfToken(): Promise<string> {
  if (csrfRequest) return csrfRequest;

  csrfRequest = (async () => {
    const response = await fetch('/api/csrf-token', { credentials: 'include' });
    if (!response.ok) {
      throw new ApiError(response.status, "Impossible d'obtenir le jeton de sécurité.");
    }
    const body = (await response.json()) as { token: string };
    csrfToken = body.token;
    return body.token;
  })();

  try {
    return await csrfRequest;
  } finally {
    csrfRequest = null;
  }
}

/**
 * Redirige la page entière vers l'ouverture de session OIDC.
 * Une redirection de page — pas un `fetch` : le fournisseur d'identité doit
 * pouvoir afficher son propre formulaire.
 */
function redirectToLogin(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
}

/** Valeurs acceptées dans une chaîne de requête. */
export type QueryValue = string | number | boolean | null | undefined;

/** Options d'une requête. */
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

/** Construit le chemin final avec sa chaîne de requête. */
export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${path}?${serialised}` : path;
}

/** Corps d'erreur normalisé renvoyé par l'API. */
interface ErrorBody {
  message?: string;
  issues?: Array<string | ApiIssue>;
}

/** Normalise le champ `issues`, que l'API expose tantôt en objets, tantôt en chaînes. */
function normaliseIssues(issues: ErrorBody['issues']): ApiIssue[] | undefined {
  if (!issues || issues.length === 0) return undefined;
  return issues.map((issue) => (typeof issue === 'string' ? { message: issue } : issue));
}

/** Lit le corps de la réponse, en tolérant un corps vide ou non JSON. */
async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Exécute une requête, sans la logique de rejeu CSRF. */
async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (MUTATING_METHODS.has(method)) {
    headers['x-csrf-token'] = csrfToken ?? (await fetchCsrfToken());
  }

  return fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'include',
    signal: options.signal,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });
}

/**
 * Point d'entrée unique. Le type de retour est celui du DTO attendu : les
 * fonctions de `endpoints.ts` le renseignent, aucun appelant ne manipule
 * d'`unknown`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const isMutation = MUTATING_METHODS.has(method);

  let response: Response;
  try {
    response = await performRequest(path, options);
  } catch (error) {
    // Une annulation volontaire n'est pas une panne : on la propage telle quelle
    // pour que l'appelant puisse l'ignorer.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'Le portail est injoignable. Vérifiez votre connexion réseau, puis réessayez.',
    );
  }

  if (response.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'Votre session a expiré. Redirection vers la page de connexion…');
  }

  let body = await readBody(response);

  // Un 403 dû à un jeton CSRF périmé se répare tout seul : on en redemande un
  // et on rejoue la requête, une seule fois. Les autres 403 (droits
  // insuffisants) doivent au contraire remonter intacts.
  const errorBody = (typeof body === 'object' && body !== null ? body : {}) as ErrorBody;
  if (response.status === 403 && isMutation && /csrf/i.test(errorBody.message ?? '')) {
    csrfToken = null;
    await fetchCsrfToken();
    try {
      response = await performRequest(path, options);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError(0, 'Le portail est injoignable. Réessayez dans un instant.');
    }
    body = await readBody(response);
  }

  if (!response.ok) {
    const failure = (typeof body === 'object' && body !== null ? body : {}) as ErrorBody;
    throw new ApiError(
      response.status,
      failure.message ?? `La requête a échoué (code ${response.status}).`,
      normaliseIssues(failure.issues),
    );
  }

  return body as T;
}

/**
 * Déclenche le téléchargement d'un fichier servi par une route GET (exports
 * CSV/JSON). On passe par un lien plutôt que par `fetch` : le navigateur
 * exploite alors l'en-tête `Content-Disposition` renvoyé par l'API, et la
 * session est jointe automatiquement.
 */
export function downloadFile(path: string, query?: Record<string, QueryValue>): void {
  const anchor = document.createElement('a');
  anchor.href = buildUrl(path, query);
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
